import { Badge, Box, Button } from "@mui/material";
import StatusChip from "../../../../../../../../components/StatusChip";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../../components/Flex";
import PdfViewDrawer from "../../../../../../../../components/PdfViewDrawer";
import RenderWithTooltip from "../../../../../../../../components/RenderWithTooltip";
import StyledDialog from "../../../../../../../../components/StyledDialog";
import Text from "../../../../../../../../components/Text";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import { DisclosureHost } from "../../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../../hooks/use-reduce-disclosure";
import { useArchiveOrder } from "../../../../../../../../queries/patient-chart/order/use-archive-order";
import { useGetOrders } from "../../../../../../../../queries/patient-chart/order/use-get-orders";
// import { useOrderReportDownload } from "../../../../../../../../queries/patient-chart/order/use-order-report-download";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { usePatientOrderStatusUpdate } from "../../../../../../../../queries/patient-chart/order/use-patient-order-status-update";
import {
  NonNullableDate,
  formatDateTime,
} from "../../../../../../../../utils/date-time-utility";
import {
  DEFAULT_UUID_NAME_LIST,
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getFormattedId,
  getSuccessSnackbar,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
  transformMultiSelectFrontEndVal,
} from "../../../../../../../../utils/utils";
import UpsertLabRequisitionForm from "../../../../../scheduling/appointment/encounter/default-forms/UpsertLabRequisitionForm";
import {
  getOrderActionOptions,
  VIEW_ORDER_REPORT_ACTION,
} from "../order-utilsnew";
import UpsertResult from "../result/UpsertResultNew";
import PrintOrders from "./PrintOrders";
import { getStoredRealm } from "@/utils/auth-storage";

const PatientOrder = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { mutateAsync: archiveOrder } = useArchiveOrder();
  const deleteOrderDisc = useDeleteDisClosure();
  const showSnackbar = useSnackbar();
  const { patientId } = useParams();
  const [
    previewResultDisc,
    orderDisc,
    showMoreDocDisc,
    changeStatusDisc,
    labResult,
    orderPrintViewDisclosure,
  ] = useReduceDisclosure(6);
  const { hasView } = useRoleController("Imaging order");
  // const { mutateAsync: downloadOrderReport } = useOrderReportDownload();
  const { mutateAsync: changeStatus } = usePatientOrderStatusUpdate();

  const { data: orders, refetch } = useGetOrders({
    patientUuid: patientId ?? "",
    archive: false,
    pagination,
    gridSorting,
    xTENANTID: getXtenantId(),
  });

  const getOrderPrint = async (orderId: string) => {
    orderPrintViewDisclosure.open(orderId);
  };

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "uuid",
        headerName: "Order ID",
        flex: 0.3,
        width: 100,
        renderCell: ({ row }) => {
          return <Text isTabletext>{getFormattedId(row?.uuid)}</Text>;
        },
      },
      {
        field: "orderTitle",
        headerName: "Title",
        flex: 0.4,
        width: 140,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      // new field added as per figma design
      {
        field: "labs",
        headerName: "Lab/Imaging Center",
        flex: 0.4,
        width: 140,
        renderCell: ({ value, row }) => (
          <RenderWithTooltip
            value={
              row.orderFormType === "ELECTRONIC"
                ? row.imagingCenter
                : getValueFromObj(value)
            }
          />
        ),
      },
      {
        field: "orderFormType",
        headerName: "Mode",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) =>
          value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
      },
      {
        field: "orderType",
        headerName: "Type",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ value }) =>
          value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
      },
      {
        field: "appointmentDate",
        headerName: "Appointment",
        flex: 0.8,
        width: 200,
        renderCell: ({ row }) =>
          row?.appointmentDate ? (
            <Flex gap={1} flexDirection={"column"}>
              {/* <Text isTabletext>{getFormattedId(row?.appointmentId)}</Text> */}
              <Text isTabletext>{formatDateTime(row?.appointmentDate)}</Text>
            </Flex>
          ) : (
            "-"
          ),
      },
      {
        field: "providerName",
        headerName: "Prescribed By",
        flex: 0.5,
        width: 160,
        renderCell: ({ row }) =>
          row?.userName ? <RenderWithTooltip value={row?.userName} /> : "-",
      },
      {
        field: "collection_date_time",
        headerName: "Date",
        flex: 0.4,
        width: 140,
        renderCell: ({ row }) => NonNullableDate(row?.collectionDateTime),
      },
      // {
      //   field: "labInstruction",
      //   headerName: "Description",
      //   flex: 0.5,
      //   width: 160,
      //   renderCell: ({ value }) => (
      //     <RenderWithTooltip value={value} />
      //   ),
      // },
      {
        field: "labResults",
        headerName: "Document",
        flex: 0.5,
        width: 160,
        renderCell: ({ value }) =>
          value?.length > 0 ? (
            <Text
              isTabletext
              sx={{ cursor: "pointer" }}
              color={"primary.main"}
              onClick={() =>
                value?.length === 1
                  ? previewResultDisc.open(value[0]?.labDocument)
                  : showMoreDocDisc.open(value)
              }
            >
              <Flex gap={2} alignItems={"center"}>
                <Box
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    previewResultDisc.open(value[0]?.labDocument);
                  }}
                >
                  <RenderWithTooltip value={value?.[0]?.labDescription} />
                </Box>

                {value?.length > 1 && (
                  <Badge
                    color="primary"
                    badgeContent={`+${value.length - 1}`}
                    max={999}
                    showZero
                  />
                )}
              </Flex>
            </Text>
          ) : (
            "-"
          ),
      },
      {
        field: "orderStatus",
        headerName: "Status",
        flex: 0.3,
        width: 120,
        renderCell: ({ row }) => {
          if (!row?.orderStatus) return "-";
          const statusVariantMap: Record<
            string,
            | "success"
            | "error"
            | "warning"
            | "info"
            | "default"
            | "neutral"
            | "pending"
          > = {
            Completed: "success",
            Cancelled: "error",
            Pending: "pending",
            Draft: "neutral",
            Printed: "info",
          };
          return (
            <StatusChip
              label={row.orderStatus}
              variant={statusVariantMap[row.orderStatus] ?? "default"}
            />
          );
        },
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 90,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          // The Actions menu is driven by the order status alone — see
          // getOrderActionOptions for the per-status list. The visit note
          // embeds this table in its minimized, read-only form where the column
          // is a single view icon, so it keeps the report action for every row.
          const isVisitNote = window.location.pathname.includes("/visit-note");
          const actionOptions = isVisitNote
            ? [VIEW_ORDER_REPORT_ACTION]
            : roleFilter({
                list: getOrderActionOptions(row?.orderStatus),
                compWith: { view: hasView, viewOrder: hasView },
              });

          // A canceled order has no actions at all — render an empty cell
          // instead of a menu with nothing in it.
          if (!actionOptions.length) return null;

          return (
            <ActionMenu
              options={actionOptions}
              onActionClick={(v) => {
                if (v === "edit") {
                  const transformedData = {
                    ...row,
                    location: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      row?.location
                    ),
                    labs: transformMultiSelectFrontEndVal(
                      DEFAULT_VALUE_LABEL_LIST,
                      row?.labs
                    ),
                    tests: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      row?.tests
                    ),
                    medicalCodes: transformMultiSelectFrontEndVal(
                      DEFAULT_UUID_NAME_LIST,
                      row?.medicalCodes
                    ),
                    imagingCenter: row?.imagingCenter ?? undefined,
                    primaryProvider: row?.primaryProvider
                      ? {
                          value: Object.keys(row.primaryProvider)[0],
                          label: Object.values(row.primaryProvider)[0],
                        }
                      : undefined,
                    orderingProvider: row?.orderingProvider
                      ? {
                          value: Object.keys(row.orderingProvider)[0],
                          label: Object.values(row.orderingProvider)[0],
                        }
                      : undefined,
                    patientInstructions: row?.patientInstruction ?? undefined,
                  };
                  orderDisc.open(transformedData);
                  return;
                }
                // "View Order" (placed orders) and "View & Download Report"
                // (completed orders) both open the printable order.
                if (v === "view" || v === "viewOrder") {
                  getOrderPrint(row?.uuid);
                }
                // Delete is no longer offered on any status — the menu is
                // limited to the actions listed per status on the ticket.
                // else if (v === "delete") deleteOrderDisc.open(row?.uuid);
                else if (v === "addResult") {
                  const initialValues = {
                    file: undefined,
                    labDescription: "",
                    recordedDate: undefined,
                    abnormalFlag: undefined,
                    note: "",
                    resultValue: "",
                    patientOrderId: {
                      value: row?.uuid,
                      label: row?.orderTitle,
                    },
                  };
                  labResult.open(initialValues);
                } else
                  changeStatusDisc.open({
                    patientOrderId: row?.uuid,
                    status: v,
                  });
              }}
            />
          );
        },
      },
    ],
    ["collection_date_time"]
  );
  async function archiveHandler<T>(value: boolean, id: T) {
    const res = await archiveOrder({
      patientOrderId: id?.toString() ?? "",
      status: value,
      xTENANTID: getStoredRealm() ?? undefined,
    });
    refetch();
    return res;
  }

  async function changeStatusHandler(uuid: string, status: string) {
    const res = await changeStatus({
      patientOrderId: uuid,
      status: status,
      xTENANTID: getXtenantId() ?? undefined,
    });
    refetch();
    changeStatusDisc.close();
    return res;
  }

  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const MINIMIZED_ORDER_FIELDS = [
    "orderTitle",
    "orderType",
    "orderStatus",
    "Action",
  ];
  const filteredColumns = isVisitNotePage
    ? COLUMNS.filter((col: any) => MINIMIZED_ORDER_FIELDS.includes(col.field))
    : COLUMNS;

  return (
    <>
      <UpsertResult disclosureHost={labResult} />
      {orderPrintViewDisclosure.data && (
        <PrintOrders disclosureHost={orderPrintViewDisclosure} />
      )}

      <DeleteModal
        w="380px"
        open={deleteOrderDisc.opened}
        onClose={deleteOrderDisc.close}
        id={deleteOrderDisc.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this order"}
      />

      {/* //modal to change the status of the order */}
      <DeleteModal
        w="380px"
        open={changeStatusDisc.opened}
        onClose={changeStatusDisc.close}
        id={changeStatusDisc.data?.patientOrderId}
        onConfirmation={(uuid) =>
          changeStatusHandler(uuid, changeStatusDisc.data?.status).then(
            (res) => {
              showSnackbar(getSuccessSnackbar(res?.data?.message));
              return res;
            }
          )
        }
        content={`You want to change the status of this order to ${changeStatusDisc.data?.status}`}
      />

      <ShowMoreDrawer
        disclosureHost={showMoreDocDisc}
        previewResultDisc={previewResultDisc}
      />

      <UpsertLabRequisitionForm disclosureHost={orderDisc} />

      <PdfViewDrawer
        w="65vw"
        file={previewResultDisc?.data}
        disclosureHost={previewResultDisc}
        title="Order Result"
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={orders?.data?.content ?? []}
        rowCount={orders?.data?.page?.totalElements ?? 10}
        columns={filteredColumns}
        rowHeight={50}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default PatientOrder;

const ShowMoreDrawer = ({
  disclosureHost,
  previewResultDisc,
}: {
  disclosureHost: DisclosureHost;
  previewResultDisc: DisclosureHost;
}) => (
  <StyledDialog
    open={disclosureHost.opened}
    onClose={disclosureHost.close}
    dialogTitle="All Results"
  >
    <Flex flexDirection={"column"} gap={1} width={"30vw"}>
      {disclosureHost?.data?.map((it: any) => (
        <Flex
          alignItems={"center"}
          width={"100%"}
          justifyContent={"space-between"}
        >
          <Text
            sx={{ cursor: "pointer" }}
            color={"primary.main"}
            onClick={() => previewResultDisc.open(it?.labDocument)}
          >
            {it?.labDescription}
          </Text>
          <Button
            variant="contained"
            onClick={() => previewResultDisc.open(it?.labDocument)}
          >
            View
          </Button>
        </Flex>
      ))}
    </Flex>
  </StyledDialog>
);
